/**
 * Register the Root Flame Grove service worker and emit an event when a new
 * version is ready to take over. The UpdateBanner component listens for this
 * event and shows a small "a new breath has arrived" prompt to the user.
 *
 * Strategy:
 *   1. On page load, register /service-worker.js
 *   2. Periodically call registration.update() to check for new SW versions
 *      (default: every 30 minutes while the app is open)
 *   3. When a new SW finishes installing AND there's an active controller,
 *      dispatch a "rfg:sw-update-ready" event on window
 *   4. When the user accepts the update, post SKIP_WAITING to the new SW,
 *      then reload once it takes control
 */

const SW_PATH = "/service-worker.js";
const UPDATE_CHECK_INTERVAL_MS = 30 * 60 * 1000; // 30 minutes

let refreshing = false;

export function registerGroveServiceWorker() {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;
  // Avoid running on dev origins where SW would interfere with hot-reload.
  // CRA's dev server serves from localhost or :3000 — skip those.
  if (
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1"
  ) {
    return;
  }

  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register(SW_PATH)
      .then((registration) => {
        // Periodic check while the app is open
        setInterval(() => {
          registration.update().catch(() => {});
        }, UPDATE_CHECK_INTERVAL_MS);

        // If a waiting worker is already there on first registration
        if (registration.waiting && navigator.serviceWorker.controller) {
          notifyUpdateReady(registration);
        }

        // Detect updates that arrive after initial registration
        registration.addEventListener("updatefound", () => {
          const newWorker = registration.installing;
          if (!newWorker) return;
          newWorker.addEventListener("statechange", () => {
            if (
              newWorker.state === "installed" &&
              navigator.serviceWorker.controller
            ) {
              notifyUpdateReady(registration);
            }
          });
        });
      })
      .catch((err) => {
        // SW failed to register — the app still works, just without offline / auto-update
        // (Browser console will have the actual error)
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.warn("SW registration failed:", err);
        }
      });

    // When the new SW takes control after SKIP_WAITING, reload exactly once.
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (refreshing) return;
      refreshing = true;
      window.location.reload();
    });
  });
}

function notifyUpdateReady(registration) {
  window.dispatchEvent(
    new CustomEvent("rfg:sw-update-ready", { detail: { registration } })
  );
}

/** Apply the waiting update. Called from the banner button. */
export function applyGroveUpdate(registration) {
  if (!registration || !registration.waiting) {
    // No waiting worker — just force a hard reload as a fallback.
    window.location.reload();
    return;
  }
  registration.waiting.postMessage({ type: "SKIP_WAITING" });
}
